import React, {
  useState,
  useRef,
  useCallback,
  CSSProperties,
  useEffect,
} from "react";
import RemstalVoiceWidget from "./RemstalVoiceWidget";
import type { VoiceWidgetConfig } from "../hooks/useVoiceWidget";
import {
  injectPulseAnimations,
  getAnimationForState,
  BADGE_COLORS,
} from "../utils/pulseAnimations";

interface AvatarConfig {
  id: string;
  agentId: string;
  name: string;
  imageUrl: string;
  description?: string;
}

interface MultiWidgetSpeedDialProps {
  mainAvatar: AvatarConfig;
  subAvatars: [AvatarConfig, AvatarConfig, AvatarConfig];
  position?: "bottom-left" | "bottom-right" | "top-left" | "top-right";
  baseConfig?: Partial<VoiceWidgetConfig>;
  className?: string;
}

const MultiWidgetSpeedDial: React.FC<MultiWidgetSpeedDialProps> = ({
  mainAvatar,
  subAvatars,
  position = "bottom-left",
  baseConfig = {},
  className = "",
}) => {
  // All available avatars (main + sub)
  const allAvatars = [mainAvatar, ...subAvatars];

  const [isExpanded, setIsExpanded] = useState(false);
  const [currentAvatar, setCurrentAvatar] = useState<AvatarConfig>(mainAvatar);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [widgetKey, setWidgetKey] = useState(0);
  const [isWidgetActive, setIsWidgetActive] = useState(false);

  // Prevent concurrent switching
  const switchingRef = useRef<boolean>(false);
  const widgetTriggerFnRef = useRef<(() => void) | null>(null);

  // Calculate which avatars should appear in the speed dial (exclude current)
  const getSpeedDialAvatars = (): [
    AvatarConfig,
    AvatarConfig,
    AvatarConfig
  ] => {
    const availableAvatars = allAvatars.filter(
      (avatar) => avatar.id !== currentAvatar.id
    );
    return availableAvatars as [AvatarConfig, AvatarConfig, AvatarConfig];
  };

  const speedDialAvatars = getSpeedDialAvatars();

  // Inject pulse animation styles
  useEffect(() => {
    injectPulseAnimations("multi-widget-speed-dial-styles");
  }, []);

  const handleWidgetStateChange = useCallback(
    (state: {
      connected: boolean;
      recording: boolean;
      isAgentSpeaking: boolean;
    }) => {
      // Widget is considered active if connected and either recording or agent is speaking
      const active =
        state.connected && (state.recording || state.isAgentSpeaking);
      setIsWidgetActive(active);

      // Collapse speed dial when widget becomes active
      if (active && isExpanded) {
        setIsExpanded(false);
      }
    },
    [isExpanded]
  );

  const handleWidgetReady = useCallback((triggerFn: () => void) => {
    console.log(`[MultiWidget] Widget ready, storing trigger function`);
    widgetTriggerFnRef.current = triggerFn;
  }, []);

  const switchToAvatar = async (avatar: AvatarConfig) => {
    // Prevent concurrent switches
    if (
      switchingRef.current ||
      isTransitioning ||
      avatar.id === currentAvatar.id
    ) {
      console.log(
        `[MultiWidget] Switch blocked - already switching or same avatar`
      );
      return;
    }

    console.log(
      `[MultiWidget] Switching from ${currentAvatar.name} to ${avatar.name}`
    );

    // Set switching flag
    switchingRef.current = true;
    setIsTransitioning(true);
    setIsExpanded(false);

    // Clear widget state
    setIsWidgetActive(false);

    // If widget is active, trigger stop before switching
    if (isWidgetActive && widgetTriggerFnRef.current) {
      console.log(
        `[MultiWidget] Stopping active conversation for ${currentAvatar.name}`
      );
      try {
        // Trigger the button to stop the conversation
        widgetTriggerFnRef.current();
        // Give it a moment to stop
        await new Promise((resolve) => setTimeout(resolve, 200));
      } catch (error) {
        console.error(`[MultiWidget] Error stopping conversation:`, error);
      }
    }

    // Clear the stored trigger function
    widgetTriggerFnRef.current = null;

    // Switch to new avatar - incrementing key forces complete remount and cleanup
    setCurrentAvatar(avatar);
    setWidgetKey((prev) => prev + 1);

    // End transition after a brief delay
    setTimeout(() => {
      setIsTransitioning(false);
      switchingRef.current = false;
    }, 400);
  };

  const getSubWidgetPosition = (index: number) => {
    const spacing = 160; // Gap between sub-widgets for 140px avatars
    const mainOffset = 280; // Much larger offset to prevent overlap with 252px main avatar

    switch (position) {
      case "bottom-left":
        return {
          bottom: `${mainOffset + index * spacing}px`,
          left: "0px",
        };
      case "bottom-right":
        return {
          bottom: `${mainOffset + index * spacing}px`,
          right: "0px",
        };
      case "top-left":
        return {
          top: `${mainOffset + index * spacing}px`,
          left: "0px",
        };
      case "top-right":
        return {
          top: `${mainOffset + index * spacing}px`,
          right: "0px",
        };
      default:
        return {
          bottom: `${mainOffset + index * spacing}px`,
          left: "0px",
        };
    }
  };

  const isAvatarDisabled = (avatarId: string) => {
    // During transition, all avatars are disabled
    if (isTransitioning) return true;

    // If widget is active, only the current avatar can be clicked (to stop)
    if (isWidgetActive) {
      return avatarId !== currentAvatar.id;
    }

    // Otherwise all avatars are enabled
    return false;
  };

  const getPositionStyles = (): CSSProperties => {
    const styles: CSSProperties = { position: "fixed", zIndex: 50 };
    switch (position) {
      case "bottom-left":
        return { ...styles, bottom: "1.5rem", left: "1.5rem" };
      case "bottom-right":
        return { ...styles, bottom: "1.5rem", right: "1.5rem" };
      case "top-left":
        return { ...styles, top: "1.5rem", left: "1.5rem" };
      case "top-right":
        return { ...styles, top: "1.5rem", right: "1.5rem" };
      default:
        return { ...styles, bottom: "1.5rem", left: "1.5rem" };
    }
  };

  const handleAvatarClick = async (avatar: AvatarConfig) => {
    // Block clicks during transitions or switching
    if (switchingRef.current || isTransitioning) {
      console.log(`[MultiWidget] Click blocked - transition in progress`);
      return;
    }

    if (isAvatarDisabled(avatar.id)) {
      return;
    }

    // If clicking the current avatar while active, we need to trigger the widget
    if (avatar.id === currentAvatar.id) {
      if (isWidgetActive) {
        // Stop the conversation
        console.log(`[MultiWidget] Stopping conversation for ${avatar.name}`);
      } else {
        // Start the conversation
        console.log(`[MultiWidget] Starting conversation for ${avatar.name}`);
      }
      // Trigger the widget button click via stored function
      if (widgetTriggerFnRef.current) {
        console.log(`[MultiWidget] Triggering widget for ${avatar.name}`);
        widgetTriggerFnRef.current();
      } else {
        console.log(
          `[MultiWidget] Widget trigger function not available yet for ${avatar.name}`
        );
      }
      return;
    }

    // Switch to different avatar
    await switchToAvatar(avatar);
  };

  // --- Inline Styles ---
  const mainContainerStyle: CSSProperties = {
    ...getPositionStyles(),
  };

  const mainAvatarWrapperStyle: CSSProperties = {
    position: "relative",
  };

  const mainAvatarButtonStyle: CSSProperties = {
    position: "relative",
    // Main avatar at 252px base size: responsive sizing
    width: "min(70vw, 252px)", // 252px base size as requested
    height: "min(70vw, 252px)",
    borderRadius: "9999px", // rounded-full
    overflow: "hidden",
    boxShadow:
      "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)", // shadow-lg
    transition: "all 300ms ease-out",
    border: "none",
    padding: 0,
    cursor: "pointer",
    ...(isExpanded && { transform: "scale(1.05)" }), // Reduced scale to avoid overflow
    ...(isWidgetActive && {
      animation: getAnimationForState("idle", true), // Pulsing animation when active
    }),
    ...(isAvatarDisabled(currentAvatar.id) && {
      opacity: 0.5,
      cursor: "not-allowed",
    }),
    ...(isTransitioning && {
      opacity: 0.3,
      transform: "scale(0.95)",
    }),
  };

  const subAvatarContainerBaseStyle: CSSProperties = {
    position: "absolute",
    transition: "all 300ms ease-out",
  };

  const subAvatarButtonStyle: CSSProperties = {
    position: "relative",
    // Larger topic widget avatars to match 252px main avatar
    width: "min(35vw, 140px)", // Scaled up to better proportion with 252px main
    height: "min(35vw, 140px)",
    borderRadius: "9999px", // rounded-full
    overflow: "hidden",
    boxShadow:
      "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)", // shadow-md
    transition: "all 200ms ease-out",
    border: "none",
    padding: 0,
    cursor: "pointer",
  };

  const imageStyle: CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  };

  const activeIndicatorOverlayStyle: CSSProperties = {
    position: "absolute",
    inset: 0,
    backgroundColor: "rgba(59, 130, 246, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const activeIndicatorDotStyle: CSSProperties = {
    width: "1.5rem", // Reasonably sized indicator dot for main avatar
    height: "1.5rem",
    backgroundColor: "#3b82f6",
    borderRadius: "9999px",
  };

  const badgeStyle: CSSProperties = {
    position: "absolute",
    top: "-0.5rem", // More reasonable position
    right: "-0.5rem",
    width: "1.5rem", // More reasonable badge size
    height: "1.5rem",
    borderRadius: "9999px",
    border: "2px solid white", // Normal border
  };

  return (
    <div
      style={mainContainerStyle}
      className={className}
      onMouseEnter={() =>
        !isWidgetActive && !isTransitioning && setIsExpanded(true)
      }
      onMouseLeave={() => setIsExpanded(false)}
    >
      {/* Main Avatar Button */}
      <div style={mainAvatarWrapperStyle}>
        <button
          onClick={() => handleAvatarClick(currentAvatar)}
          disabled={isAvatarDisabled(currentAvatar.id)}
          style={mainAvatarButtonStyle}
          title={
            isTransitioning
              ? "Switching assistants..."
              : isWidgetActive
              ? `Click to stop ${currentAvatar.name}'s conversation`
              : `Start conversation with ${currentAvatar.name}`
          }
        >
          <img
            src={currentAvatar.imageUrl}
            alt={currentAvatar.name}
            style={imageStyle}
          />
        </button>

        {/* Active indicator badge */}
        {/* {isWidgetActive && (
          <div
            style={{ ...badgeStyle, backgroundColor: BADGE_COLORS.active }}
          />
        )} */}

        {/* Transition indicator */}
        {isTransitioning && (
          <div
            style={{ ...badgeStyle, backgroundColor: BADGE_COLORS.connecting }}
          />
        )}
      </div>

      {/* Sub-widget buttons */}
      {speedDialAvatars.map((avatar, index) => (
        <div
          key={avatar.id}
          style={{
            ...subAvatarContainerBaseStyle,
            ...getSubWidgetPosition(index),
            transitionDelay: `${index * 50}ms`,
            ...(isExpanded
              ? {
                  opacity: 1,
                  transform: "translateY(0) scale(1)",
                  pointerEvents: "auto",
                }
              : {
                  opacity: 0,
                  transform: "translateY(1rem) scale(0.75)",
                  pointerEvents: "none",
                }),
          }}
        >
          <button
            onClick={() => handleAvatarClick(avatar)}
            disabled={isAvatarDisabled(avatar.id)}
            style={{
              ...subAvatarButtonStyle,
              ...(currentAvatar.id === avatar.id &&
                isWidgetActive && {
                  animation: getAnimationForState("idle", true), // Pulsing animation when active
                }),
              ...(isAvatarDisabled(avatar.id) && {
                opacity: 0.3,
                cursor: "not-allowed",
                filter: "grayscale(100%)",
              }),
              ...(isTransitioning && {
                opacity: 0.3,
                transform: "scale(0.9)",
              }),
            }}
            title={
              isTransitioning
                ? "Switching assistants..."
                : isAvatarDisabled(avatar.id) && isWidgetActive
                ? "Another assistant is active"
                : currentAvatar.id === avatar.id && isWidgetActive
                ? "Click to stop conversation"
                : avatar.name
            }
          >
            <img src={avatar.imageUrl} alt={avatar.name} style={imageStyle} />
          </button>

          {/* Active indicator badge */}
          {/* {currentAvatar.id === avatar.id && isWidgetActive && (
            <div
              style={{
                ...badgeStyle,
                width: "1rem", // More reasonable badge for sub-avatars
                height: "1rem",
                top: "-0.25rem", // Adjusted position for sub-avatars
                right: "-0.25rem",
                border: "2px solid white",
                backgroundColor: BADGE_COLORS.active,
              }}
            />
          )} */}
        </div>
      ))}

      {/* Single Voice Widget Instance - key forces remount on avatar change */}
      <div style={{ display: "none" }}>
        <RemstalVoiceWidget
          key={widgetKey}
          {...baseConfig}
          agentId={currentAvatar.agentId}
          widgetId={currentAvatar.id}
          onStateChange={handleWidgetStateChange}
          config={{
            ...baseConfig,
            agentId: currentAvatar.agentId,
            widgetId: currentAvatar.id,
          }}
          onWidgetReady={handleWidgetReady}
        />
      </div>

      {/* Backdrop to close when expanded */}
      {isExpanded && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: -10,
          }}
          onClick={() => setIsExpanded(false)}
        />
      )}
    </div>
  );
};

export default MultiWidgetSpeedDial;
export type { AvatarConfig, MultiWidgetSpeedDialProps };
